<template>
  <footer ref="footerRoot">
    <div class="footer-container">
      <div class="footer-main-container">
        <div class="footer-main-menu">
          <div class="footer-title">{{$siteConfig.site}}</div>
          <div class="footer-menu">
            <a href="/">{{$t('Home')}}</a>
            <a href="https://www.tripening.com/travel-tips" v-if="brand.feature('blog')">{{$t('Blog')}}</a>
            <a href="/" v-if="false">{{$t('Contact')}}</a>
            <a href="/terms-and-conditions">{{$t('Terms of use')}}</a>
            <a href="/privacy-policy">{{$t('Privacy & cookies')}}</a>
            <a href="/support">{{$t('Support')}}</a>
            <!-- Live chat ("Help") disabled for now — the /support contact form is the active channel. Remove v-if="false" to re-enable. -->
            <a href="/" v-if="false" @click.prevent="openLiveChat">{{$t('Help')}}</a>
            <a href="/about-us" v-if="brand.is('bookdirect')">{{$t('About us')}}</a>
          </div>
          <div class="card-brands">
            <img width="32" height="32" loading="lazy" alt="mastercard" data-src="/img/payments/mastercard.svg" src="/images/payments/mastercard.svg" lazy="loaded">
            <img width="32" height="32" loading="lazy" alt="visa" data-src="/img/payments/visa.svg" src="/images/payments/visa.svg" lazy="loaded">
            <img width="32" height="32" loading="lazy" alt="american express" data-src="/img/payments/ae.svg" src="/images/payments/ae.svg" lazy="loaded">
            <img width="32" height="32" loading="lazy" alt="discover" data-src="/img/payments/discover.svg" src="/images/payments/discover.svg" lazy="loaded">
          </div>
        </div>
        <div class="join-our-newsletter">
          <div class="footer-title">
            {{$t('Join Our Newsletter')}}
          </div>
          <div class="join-our-newsletter__desc">
            {{$t('Be the first to know about our exclusive offers and more.')}}
          </div>
          <form class="email-form">
            <input :placeholder="$t('Enter your email')"> <button aria-label="Subscribe">{{$t('Subscribe')}}</button>
          </form>
        </div>
      </div>
      <div v-if="footerDestinations.length" class="footer-destinations">
        <div class="footer-title">{{ $t('Popular Destinations') }}</div>
        <div class="footer-destinations__links">
          <a v-for="city in footerDestinations" :key="city.slug" :href="`/hotels/${city.slug}`">
            {{ city.name }}
          </a>
          <a href="/hotels/destinations" class="footer-destinations__all">{{ $t('All destinations') }}</a>
        </div>
      </div>
      <div v-if="footerExperienceDestinations.length" class="footer-destinations">
        <div class="footer-title">{{ $t('Popular Experience Destinations') }}</div>
        <div class="footer-destinations__links">
          <a v-for="city in footerExperienceDestinations" :key="city.slug" :href="`/experiences/${city.slug}`">
            {{ city.name }}
          </a>
          <a href="/experiences/destinations" class="footer-destinations__all">{{ $t('All experience destinations') }}</a>
        </div>
      </div>
      <div class="footer-bottom-container">
        <div class="copyright-text text-caption color-black">
          {{`© Copyright ${new Date().getFullYear()} - ${$siteConfig.site}`}}
        </div>
        <div class="social-links">
          <a :href="configData.twitterLink" target="_blank" rel="noopener noreferrer" v-if="configData.twitterLink">
            <img src="/images/social/x.svg" alt="X">
          </a>
          <a :href="configData.facebookLink" target="_blank" rel="noopener noreferrer" v-if="configData.facebookLink">
            <img src="/images/social/fb.svg" alt="Facebook">
          </a>
          <a :href="configData.linkedinLink" target="_blank" rel="noopener noreferrer" v-if="false">
            <img src="/images/social/linkedin.svg" alt="LinkedIn">
          </a>
          <a :href="configData.instagramLink" target="_blank" rel="noopener noreferrer" v-if="configData.instagramLink">
            <img src="/images/social/instagram.svg" alt="Instagram">
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
const { $siteConfig } = useNuxtApp()
const config = useRuntimeConfig()
const configData = config.public
const liveChat = useLiveChat()
const brand = useBrand()

interface FooterCity {
  name: string
  slug: string
  hotel_count: number
}

interface FooterExperienceCity {
  name: string
  slug: string
  activity_count: number
}

const footerDestinations = ref<FooterCity[]>([])
const footerExperienceDestinations = ref<FooterExperienceCity[]>([])

const footerRoot = ref<HTMLElement | null>(null)

// Two destination lists (~90 KB of JSON) used to load on every page the moment it mounted,
// competing with the room search on a hotel landing for the visitor's connection. The footer
// is the last thing on the page; fetch its lists when it comes within a screen of view.
onMounted(() => {
  const load = () => { void loadFooterDestinations() }
  if (typeof IntersectionObserver === 'undefined' || !footerRoot.value) {
    load()
    return
  }
  const observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      observer.disconnect()
      load()
    }
  }, { rootMargin: '800px 0px' })
  observer.observe(footerRoot.value)
})

async function loadFooterDestinations() {
  const hotelFetch = $fetch<any[]>(`${configData.baseURL}/api/product/v1/hotels/destinations`).then(data => {
    const allCities: FooterCity[] = (data || []).flatMap((c: any) => c.top_cities || [])
    allCities.sort((a, b) => b.hotel_count - a.hotel_count)
    footerDestinations.value = allCities.slice(0, 15)
  }).catch(() => {})

  const experienceFetch = $fetch<any[]>(`${configData.baseURL}/api/product/v1/experiences/destinations`).then(data => {
    const allCities: FooterExperienceCity[] = (data || []).flatMap((c: any) => c.top_cities || [])
    allCities.sort((a, b) => b.activity_count - a.activity_count)
    footerExperienceDestinations.value = allCities.slice(0, 10)
  }).catch(() => {})

  await Promise.all([hotelFetch, experienceFetch])
}

const openLiveChat = () => {
  liveChat.showLiveChat()
}
</script>

<style lang="scss" scoped>
footer {
  width: 100%;
  background-color: var(--light-grey);
  padding: 32px var(--page-horizontal-padding);

  @media screen and (max-width: 768px) {
    padding: 28px var(--page-horizontal-padding);
  }

  @media screen and (max-width: 480px) {
    padding: 24px 16px;
  }

  .footer-container {
    max-width: var(--content-max-width);
    margin: 0 auto;
    .email-form {
      display: flex;
      gap: 12px;
      justify-content: center;
      align-items: center;
      flex-wrap: wrap;
      input {
        height: 44px;
        border: 2px solid var(--grey);
        background: var(--white);
        color: var(--black);
        font-size: 16px;
        padding: 10px 16px;
        border-radius: 10px;
        width: 400px;
        max-width: 100%;
      }
      button {
        background-color: var(--blue);
        height: 44px;
        border-radius: 10px;
        color: var(--white);
        font-size: 16px;
        display: flex;
        gap: 4px;
        padding: 10px 24px;
        justify-content: center;
        align-items: center;
        font-weight: 600;
      }

      @media screen and (max-width: 600px) {
        flex-direction: column;
        align-items: stretch;

        input,
        button {
          width: 100%;
        }

        button {
          padding: 10px 16px;
        }
      }
    }
    .footer-menu {
      display: flex;
      flex-wrap: wrap;
      column-gap: 32px;
      row-gap: 12px;
      padding-top: 32px;
      padding-bottom: 32px;
      font-size: 16px;
      font-weight: 600;

      a {
        display: block;
        padding: 8px 0;
      }

      @media screen and (max-width: 768px){
        flex-direction: column;
        align-items: flex-start;
        column-gap: 0;
        row-gap: 8px;
        padding-top: 16px;
        padding-bottom: 16px;
      }
    }
    .social-links {
      display: flex;
      gap: 20px;
      justify-content: center;
      flex-wrap: wrap;

      @media screen and (max-width: 768px){
        margin-bottom: 32px;
      }

      img {
        :root[data-theme="dark"] & {
          filter: brightness(0) invert(1);
        }
      }
    }
  }

  .footer-title {
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
  }
  .footer-destinations {
    border-bottom: 1px solid var(--dark-grey);
    padding: 24px 0;

    &__links {
      display: flex;
      flex-wrap: wrap;
      gap: 6px 20px;
      padding-top: 12px;

      a {
        font-size: 14px;
        color: inherit;
        text-decoration: none;
        padding: 4px 0;

        &:hover {
          text-decoration: underline;
        }
      }

      &__all {
        font-weight: 600;
      }
    }
  }

  .footer-main-container {
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid var(--dark-grey);
    padding-bottom: 32px;

    @media screen and (max-width: 768px){
      flex-direction: column;
      gap: 32px;
    }
  }
  .join-our-newsletter {
    max-width: 440px;
    text-align: left;

    &__desc {
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      text-align: left;
      text-underline-position: from-font;
      text-decoration-skip-ink: none;
      margin-top: 4px;
      margin-bottom: 32px;
      color: var(--dark-grey);
    }

    @media screen and (max-width: 768px){
      margin-top: 32px;
    }
  }

  .footer-bottom-container {
    display: flex;
    justify-content: space-between;
    padding-top: 32px;

    @media screen and (max-width: 768px){
      flex-direction: column-reverse;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      text-align: center;
    }
  }

  .card-brands {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;

    img {
      width: 42px;
      height: 28px;
      object-fit: contain;
      padding: 4px 6px;
      border-radius: 6px;
      border: 1px solid var(--grey-border);
      background: var(--white);

      :root[data-theme="dark"] & {
        background: #fff;
        border-color: rgba(255, 255, 255, 0.15);
      }
    }
  }

  @media only screen and (min-width: 768px) and (max-width:1023px) {
    .card-brands {
      margin-top:23px;
      grid-gap: 16px;
      gap: 16px
    }
  }

}

</style>
